export const ACCESS_CONTEXT_UPDATED_EVENT = 'weekly-access-context-updated';
export const ACCESS_CONTEXT_UPDATED_AT_KEY = 'weekly-access-context-updated-at';

export type AccessContextUpdateDetail = {
  updatedAt: string;
  reason: 'user-info-saved';
};

export const readAccessContextUpdatedAtMs = (): number | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ACCESS_CONTEXT_UPDATED_AT_KEY);
    if (!raw) return null;
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * 課・チームなど、週報の閲覧可否に影響するユーザー情報の保存成功を通知する。
 * localStorage は再表示時のキャッシュ失効、CustomEvent は表示中画面の即時更新に使う。
 */
export const notifyAccessContextUpdated = (): AccessContextUpdateDetail => {
  const detail: AccessContextUpdateDetail = {
    updatedAt: new Date().toISOString(),
    reason: 'user-info-saved',
  };

  if (typeof window === 'undefined') return detail;

  try {
    window.localStorage.setItem(ACCESS_CONTEXT_UPDATED_AT_KEY, detail.updatedAt);
  } catch {
    // localStorage が使えなくても、同一画面への通知は続行する。
  }

  window.dispatchEvent(
    new CustomEvent<AccessContextUpdateDetail>(ACCESS_CONTEXT_UPDATED_EVENT, {
      detail,
    })
  );
  return detail;
};

export const getLatestWeeklyDataInvalidationMs = (
  weeklyReportUpdatedAtMs: number | null
): number => Math.max(
  weeklyReportUpdatedAtMs ?? 0,
  readAccessContextUpdatedAtMs() ?? 0
);

/**
 * 週報検索結果キャッシュが、週報更新または閲覧条件更新より古いか判定する。
 * 更新通知が無い場合は従来どおりキャッシュを利用できる。
 */
export const isWeeklyDataCacheStale = (
  cacheSavedAt: string | number | null | undefined,
  weeklyReportUpdatedAtMs: number | null = null
): boolean => {
  const invalidatedAtMs = getLatestWeeklyDataInvalidationMs(
    weeklyReportUpdatedAtMs
  );
  if (invalidatedAtMs <= 0) return false;

  const savedAtMs =
    typeof cacheSavedAt === 'number'
      ? cacheSavedAt
      : cacheSavedAt
        ? Date.parse(cacheSavedAt)
        : Number.NaN;

  return !Number.isFinite(savedAtMs) || invalidatedAtMs > savedAtMs;
};
